import React, { useEffect, useMemo, useRef } from "react";
import { Box, Flex, Stack, Text } from "@commonsku/design-system";
import type { ChatMessage, MessageFeedback } from "../../types/skubot";
import { ChatMessageItem } from "./ChatMessageItem";
import { ChatInput } from "./ChatInput";
import LoadingGear from "../shop/Container/LoadingGear";

const disclaimer = "skubot can make mistakes. Please double-check responses.";

export interface ChatMessagesProps {
  messages: ChatMessage[];
  input: string;
  onInputChange: (value: string) => void;
  onSend: (message?: string) => void;
  onFeedback?: (messageId: string, feedback: MessageFeedback) => void;
  disabled?: boolean;
  isLoading?: boolean;
  errorMessage?: string | null;
  className?: string;
  placeholder?: string;
  disclaimer?: string;
  threadHeader?: React.ReactNode;
}

export function ChatMessages(props: ChatMessagesProps) {
  const {
    messages,
    input,
    onInputChange,
    onSend,
    onFeedback,
    disabled = false,
    isLoading = false,
    errorMessage = null,
    className,
    placeholder = "Reply...",
    disclaimer: disclaimerText = disclaimer,
    threadHeader,
  } = props;
  const lastMessage = useMemo(() => {
    if (isLoading) return null;
    if (errorMessage) return errorMessage;

    const found = messages[messages.length - 1];
    if (!found) return null;
    if (found.role === 'user') return null;
    return found.content;
  }, [messages, isLoading, errorMessage]);

  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, lastMessage]);

  const showLoading = isLoading && messages.length === 0;

  return (
    <Flex className={className} flex="1" minH={0} direction="column">
      {/* Scrollable message thread */}
      <Box flex="1" minH={0} overflowY="auto" w="full"
        {...(showLoading ? {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        } : {})}
      >
        <Stack
          className="skubot-chat-thread"
          w="full"
          maxW="700px"
          mx="auto"
          gap="24px"
          px={{ base: "5", md: "10" }}
          py="24px"
        >
          {showLoading ? <LoadingGear /> : <>
            {threadHeader}
            {messages.map((message, i) => {
              const showSkuBot = message.role !== 'user' && i === (messages.length - 1) && !isLoading && !errorMessage;
              const messageLoading = message.role !== 'user' && i === messages.length - 1 && isLoading;
              const showFeedback = message.role !== 'user' && !messageLoading && !!onFeedback;
              return (
                <ChatMessageItem
                  key={message.id}
                  message={message}
                  isLoading={messageLoading}
                  onPositiveFeedback={showFeedback ? () => onFeedback(message.id, 'positive') : undefined}
                  onNegativeFeedback={showFeedback ? () => onFeedback(message.id, 'negative') : undefined}
                  showSkuBot={showSkuBot}
                />
              );
            })}
            {errorMessage && (
              <ChatMessageItem
                message={{ id: "error", role: "assistant", content: errorMessage, conversationId: "", createdAt: new Date().toISOString() }}
                isLoading={false}
                errorMessage={errorMessage}
                showSkuBot={true}
              />
            )}
            <div ref={bottomRef} />
          </>}
        </Stack>
      </Box>

      {/* Sticky input */}
      <Box className="skubot-chat-input-section" w="full" bg="#fafafa" pb="16px">
        <Stack
          className="skubot-chat-input"
          w="full"
          maxW="700px"
          mx="auto"
          align="center"
          gap="16px"
          px={{ base: "5", md: "10" }}
        >
          <ChatInput
            value={input}
            onChange={onInputChange}
            onSubmit={() => onSend()}
            placeholder={placeholder}
            disabled={disabled}
          />
          <Text
            margin="0"
            fontSize="xs"
            lineHeight="16px"
            color="#52525b"
          >
            {disclaimerText}
          </Text>
        </Stack>
      </Box>
    </Flex>
  );
}
